import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Feather } from "@expo/vector-icons";

import { AppUpdateType, type UpdateComparison } from "@/schemas/app/update";
import { showAppUpdateAlert } from "@/utils/alerts/app_updates";

type Props = {
  updateType: AppUpdateType;
  comparison: UpdateComparison;
};

type UpdateToolTipDict = {
  [key in AppUpdateType]: string;
};

const APP_UPDATE_TOOL_TIP: UpdateToolTipDict = {
  EXPO: "Quick updates for bug fixes and minor improvements. ",
  APP: "Major updates that may include new features. These require downloading from the app store.",
};

function AppUpdateSection({
  updateType,
  comparison,
}: Props): React.JSX.Element {
  const title =
    updateType === AppUpdateType.EXPO ? "Minor Updates" : "Major Updates";

  // Update button handler
  const handleUpdate = (): void => {
    if (updateType === AppUpdateType.APP) {
      showAppUpdateAlert(true);
    } else {
      // Todo
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>{title}</Text>
        <Feather name="info" size={18} color="#3b82f6" />
      </View>
      <Text style={styles.toolTip}>{APP_UPDATE_TOOL_TIP[updateType]}</Text>
      <View style={styles.cardContent}>
        <View style={styles.versionInfo}>
          <View>
            <Text style={styles.versionLabel}>Current Version</Text>
            <Text style={styles.versionNumber}>
              {comparison.currentVersion}
            </Text>
          </View>
          <View style={styles.alignRight}>
            <Text style={styles.versionLabel}>Latest Version</Text>
            <Text style={styles.versionNumber}>{comparison.latestVersion}</Text>
          </View>
        </View>

        {comparison.isUpdateAvailable ? (
          <TouchableOpacity onPress={handleUpdate}>
            <View style={styles.upToDate}>
              <Feather name="alert-circle" size={20} color="#FFFFFF" />
              <Text style={styles.updateText}>
                {updateType === AppUpdateType.APP
                  ? "Update available in the app store"
                  : "Update available"}
              </Text>
            </View>
          </TouchableOpacity>
        ) : (
          <View style={styles.upToDate}>
            <Feather name="check-circle" size={20} color="white" />
            <Text style={styles.updateText}>You are on the latest version</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  toolTip: {
    fontSize: 14,
    color: "#4b5563",
    marginBottom: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  cardContent: {
    // Content styles
  },
  versionInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  versionLabel: {
    fontSize: 12,
    color: "#6b7280",
  },
  versionNumber: {
    fontSize: 16,
    fontWeight: "bold",
  },
  alignRight: {
    alignItems: "flex-end",
  },
  upToDate: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#4299E1",
    padding: 16,
    borderRadius: 4,
  },
  updateText: {
    color: "white",
    marginLeft: 8,
  },
});

export default AppUpdateSection;
